import React, { useEffect, useState } from 'react';
import lozad from 'lozad';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import {
  sendAmplitudeAnalyticEvent,
  sendRudderStackAnalyticEvent,
} from '@utils/analytics';
import { ContextualCardType } from 'src/types';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import {
  getYoutubeVideoId,
  getPreviewUrl,
  getYoutubeSrcDoc,
} from '@utils/youtube-embed';
import useSharedState from '@invideoio/web-shared/hooks/useSharedState';
import { ClickCtaUxSource, ClickCtaType } from '../../../types/analytics';

export interface BlogBody {
  blogBody: string;
  contextualCards: ContextualCardType[];
  platform: PlatformType;
}

const lazyLoadMedia = (platform: PlatformType) => {
  if (document) {
    const allYTFrames = document?.querySelectorAll<HTMLIFrameElement>(
      'iframe[loading="lazy"][data-src^="https://www.youtube.com/embed"]',
    );
    const allOtherFrames = document?.querySelectorAll<HTMLIFrameElement>(
      'iframe[loading="lazy"]:not([data-src^="https://www.youtube.com/embed"])',
    );
    const getAllImages = document?.querySelectorAll('.lazyload');

    if (allYTFrames.length > 0) {
      allYTFrames.forEach(youtubeFrame => {
        const videoUrl = youtubeFrame.dataset.src || '';
        const videoId = getYoutubeVideoId(youtubeFrame.dataset.src || '');
        const ytPreviewImg = getPreviewUrl(videoId, platform);

        youtubeFrame.srcdoc = getYoutubeSrcDoc(videoUrl, ytPreviewImg);
        youtubeFrame.src = videoUrl;
        youtubeFrame.allow =
          'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
        return youtubeFrame;
      });
    }
    if (allOtherFrames) {
      const observer = lozad(allOtherFrames); // lazy loads elements with default selector as '.lozad'
      observer.observe();
    }
    if (getAllImages) {
      const observer = lozad(getAllImages); // lazy loads elements with default selector as '.lozad'
      observer.observe();
    }
  }
};

export const BlogBody = ({ blogBody, contextualCards, platform }: BlogBody) => {
  const [hasUserLoggedIn, setHasUserLoggedIn] = useState(true);

  const { state } = useSharedState();

  const onCtaClicked = (event: MouseEvent) => {
    const ctaText = (event?.currentTarget as HTMLAnchorElement).innerText;
    sendAmplitudeAnalyticEvent('Click Signup', {
      btnPosition: 'Blog inline card',
      'CTA text': ctaText,
    });
    sendRudderStackAnalyticEvent({
      event_name: 'click_cta',
      event_properties: {
        ux_source: ClickCtaUxSource.BlogInlineCard,
        type: ClickCtaType.ClickSignup,
        feature_utm: 'na',
      },
    });
  };

  useEffect(() => {
    if (state.user) {
      setHasUserLoggedIn(true);
    }
  }, [state.user]);

  useEffect(() => {
    lazyLoadMedia(platform);
  }, []);

  useEffect(() => {
    contextualCards?.forEach(card => {
      const elem = document.getElementById(`contextual-card-${card.CardId}`);
      if (card?.CardVisibleTo?.LoggedInUser && hasUserLoggedIn) {
        elem?.classList.add('iv-block');
      } else if (card?.CardVisibleTo?.NonLoggedInUser && !hasUserLoggedIn) {
        elem?.classList.add('iv-block');
      } else {
        elem?.classList.add('iv-hidden');
      }
    });
  }, [hasUserLoggedIn, blogBody, contextualCards]);

  useEffect(() => {
    const ctxCards = document.querySelectorAll('.ctx-card-wrapper');
    ctxCards.forEach(cardElement => {
      const cardBtn = cardElement.querySelector<HTMLAnchorElement>(
        '.contextual-card-anchor',
      )!;
      cardBtn.addEventListener('click', onCtaClicked);
    });
    return () => {
      ctxCards.forEach(cardElement => {
        const cardBtn = cardElement.querySelector<HTMLButtonElement>(
          '.contextual-card-anchor',
        )!;
        cardBtn.removeEventListener('click', onCtaClicked);
      });
    };
  }, [blogBody]);

  return (
    <div
      className={ivclass(
        'iv-blog-content',
        'iv-mb-[45px]',
        'iv-mx-[8px] sm:iv-mx-20',
        'sm:iv-overflow-hidden',
      )}
      dangerouslySetInnerHTML={{ __html: blogBody }}
    />
  );
};

export default BlogBody;
